import {
  Content,
  Description,
  Overlay,
  Portal,
  Root,
  Title,
  Trigger,
} from '@radix-ui/react-dialog';
import {
  Children,
  cloneElement,
  ComponentPropsWithoutRef,
  ElementRef,
  forwardRef,
  HTMLAttributes,
  isValidElement,
  ReactElement,
  ReactNode,
} from 'react';

import { cn } from '../../helpers/cn';
import { getComponentDisplayName } from '../../internal/react';
import { renderDefaultHeader } from './common';

const DialogOverlay = forwardRef<
  ElementRef<typeof Overlay>,
  ComponentPropsWithoutRef<typeof Overlay>
>(({ className, ...props }, ref) => (
  <Overlay
    ref={ref}
    className={cn(
      // position
      'z-modal fixed inset-0 bg-black/45',
      // overlay animations
      'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className
    )}
    {...props}
  />
));

DialogOverlay.displayName = Overlay.displayName;

export type OverlayBehavior = 'fixed' | 'scrollable' | 'none';

const DialogContent = forwardRef<
  ElementRef<typeof Content>,
  ComponentPropsWithoutRef<typeof Content> & {
    onBack?: () => void;
    variant?: 'large' | 'small';
    hideCloseBtn?: boolean;
    renderHeader?: ({
      onBack,
      hideCloseBtn,
    }: {
      onBack?: () => void;
      hideCloseBtn?: boolean;
    }) => ReactNode;
    /**
     * Controls the overlay behavior:
     * - 'fixed': Dark overlay, content scrolls inside modal (default)
     * - 'scrollable': Dark overlay that scrolls with content (for long modals)
     * - 'none': No overlay at all
     */
    overlay?: OverlayBehavior;
  }
>(
  (
    {
      className,
      children,
      variant,
      hideCloseBtn,
      onBack,
      renderHeader = renderDefaultHeader,
      overlay = 'fixed',
      ...props
    },
    ref
  ) => {
    const isScrollableOverlay = overlay === 'scrollable';
    const hasOverlay = overlay !== 'none';

    // Create content element separately to avoid Fragment wrapper around ref-forwarded component.
    // Previously used: `const Wrapper = isScrollableOverlay ? DialogOverlay : Fragment`
    // This caused "Invalid prop `ref` supplied to `React.Fragment`" errors because
    // React.Fragment cannot receive refs, but Content component forwards refs.
    // Solution: Conditionally render DialogOverlay wrapper or render content directly.
    const contentEl = (
      <Content
        ref={ref}
        className={cn(
          // position
          //
          // The centering uses an arbitrary `transform` value rather than
          // `-translate-x-1/2 -translate-y-1/2` because v4 emits those onto
          // the new individual `translate` CSS property while the slide-in
          // keyframe (`tailwindcss-animate`) writes to `transform`. In v3
          // both wrote to `transform`, so the keyframe cleanly overrode the
          // centering during animation; in v4 the two compose, producing a
          // diagonal slide and breaking Floating UI's containing-block
          // detection (which reads `transform`, not `translate`) — that
          // breaks Radix Select / Popover positioning when rendered inside
          // a modal (e.g. B2B Kontakt erstellen → Unternehmen auswählen).
          // Putting centering on `transform` lets the keyframe override it
          // during animation (vertical-only slide, matching v3) and keeps
          // a non-`none` `transform` for Floating UI to anchor against.
          'z-modal fixed top-1/2 left-1/2 [transform:translate(-50%,-50%)]',
          // layout
          'box-border flex flex-col gap-8 overflow-auto bg-white',
          // sizes
          'size-full max-h-full px-4 py-6 md:h-max md:w-[832px] md:rounded md:p-14',
          // animations
          'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] duration-200',
          {
            'md:w-[470px]': variant === 'small',
          },
          className
        )}
        {...props}
      >
        {renderHeader({ onBack, hideCloseBtn })}
        {Children.map(
          children as ReactElement<{
            className?: string;
            variant?: string;
          }>,
          (child) => {
            if (getComponentDisplayName(child) === 'ModalFooter') {
              return cloneElement(child, {
                variant,
              });
            }

            return child;
          }
        )}
      </Content>
    );

    return (
      <Portal>
        {hasOverlay && !isScrollableOverlay ? <DialogOverlay /> : null}
        {/* Conditionally wrap content in DialogOverlay (for scrollable) or render directly (for none/default).
            This avoids Fragment wrapper that would break ref forwarding. */}
        {isScrollableOverlay ? (
          <DialogOverlay>{contentEl}</DialogOverlay>
        ) : (
          contentEl
        )}
      </Portal>
    );
  }
);
DialogContent.displayName = Content.displayName;

const DialogHeader = ({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col space-y-6', className)} {...props} />
);
DialogHeader.displayName = 'DialogHeader';

const DialogFooter = ({
  className,
  children,
  variant,
  ...props
}: HTMLAttributes<HTMLDivElement> & { variant?: 'large' | 'small' }) => (
  <div
    className={cn(
      'flex size-full flex-col items-center justify-end gap-2 md:h-fit md:p-4',
      {
        'md:p-0': variant === 'small',
      },
      className
    )}
    {...props}
  >
    {Children.map(children as ReactElement<{ className?: string }>, (child) =>
      // Conditionally-rendered footer actions (`{cond ? <Button/> : null}`)
      // are a common, valid pattern — cloneElement throws on anything that
      // isn't a real element (null, but also a stray string/number child).
      isValidElement<{ className?: string }>(child)
        ? cloneElement(child, {
            className: cn(
              child.props.className,
              'w-full md:w-max md:min-w-44',
              {
                'md:w-full': variant === 'small',
              }
            ),
          })
        : child
    )}
  </div>
);
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = forwardRef<
  ElementRef<typeof Title>,
  ComponentPropsWithoutRef<typeof Title>
>(({ className, ...props }, ref) => (
  <Title
    ref={ref}
    className={cn('mobile-t4-semibold md:web-t4-semibold', className)}
    {...props}
  />
));

DialogTitle.displayName = Title.displayName;

const DialogSubtitle = forwardRef<
  ElementRef<typeof Description>,
  ComponentPropsWithoutRef<typeof Description>
>(({ className, ...props }, ref) => (
  <Description
    ref={ref}
    className={cn('body-16-light', className)}
    {...props}
  />
));
DialogSubtitle.displayName = Description.displayName;

export {
  Root as DialogRoot,
  Trigger as DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogSubtitle,
};
